import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import fetchWithCredentials from "apiClient";
import { Provider } from "types/Provider";

interface ProvidersContextType {
  providers: Provider[];
  otherCondoProviders: Provider[];
  loading: boolean;
  error: string | null;
  refresh: (condoId: number | null) => Promise<void>;
  providersCount: number;
  selectedContactId: number | null;
  setSelectedContactId: (contactId: number | null) => void;
  markContactAsRead: (contactId: number) => void;
  updateLastReadAt: (contactId: number, lastReadAt: string) => void;
}

const ProvidersContext = createContext<ProvidersContextType | undefined>(
  undefined,
);

export function ProvidersProvider({ children }: { children: ReactNode }) {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [otherCondoProviders, setOtherCondoProviders] = useState<Provider[]>(
    [],
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedContactId, setSelectedContactId] = useState<number | null>(
    null,
  );

  const fetchProviders = useCallback(async (condoId: number | null) => {
    if (!condoId) {
      setProviders([]);
      setOtherCondoProviders([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await fetchWithCredentials(
        `/manager-api/providers/?condo=${condoId}`,
      );
      setProviders(
        Array.isArray(data?.condoProviders) ? data.condoProviders : [],
      );
      setOtherCondoProviders(
        Array.isArray(data?.otherCondoProviders) ? data.otherCondoProviders : [],
      );
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Error fetching providers");
      setProviders([]);
      setOtherCondoProviders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(
    async (condoId: number | null) => {
      await fetchProviders(condoId);
    },
    [fetchProviders],
  );

  const markContactAsRead = useCallback((contactId: number) => {
    setProviders((prev) =>
      prev.map((provider) =>
        provider.contact.id === contactId
          ? { ...provider, hasUnread: false }
          : provider,
      ),
    );
    setOtherCondoProviders((prev) =>
      prev.map((provider) =>
        provider.contact.id === contactId
          ? { ...provider, hasUnread: false }
          : provider,
      ),
    );
  }, []);

  const updateLastReadAt = useCallback(
    (contactId: number, lastReadAt: string) => {
      setProviders((prev) =>
        prev.map((provider) =>
          provider.contact.id === contactId
            ? { ...provider, lastReadAt }
            : provider,
        ),
      );
      setOtherCondoProviders((prev) =>
        prev.map((provider) =>
          provider.contact.id === contactId
            ? { ...provider, lastReadAt }
            : provider,
        ),
      );
    },
    [],
  );

  const providersCount = useMemo(
    () => (Array.isArray(providers) ? providers.length : 0),
    [providers],
  );

  return (
    <ProvidersContext.Provider
      value={{
        providers,
        otherCondoProviders,
        loading,
        error,
        refresh,
        providersCount,
        selectedContactId,
        setSelectedContactId,
        markContactAsRead,
        updateLastReadAt,
      }}
    >
      {children}
    </ProvidersContext.Provider>
  );
}

export function useProviders() {
  const context = useContext(ProvidersContext);
  if (!context) {
    throw new Error("useProviders must be used within a ProvidersProvider");
  }
  return context;
}
